import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

const featureCategories = [
  {
    id: 'drawing',
    title: 'Drawing & Creativity',
    icon: '🎨',
    items: [
      {
        title: 'Doodle Drawing',
        emoji: '✏️',
        desc: 'Pure creative play. Open-ended drawing builds fine motor skills, focus, and self-expression — no rules, just joy.',
        youtube: 'F6EycER-H_Y',
      },
      {
        title: 'Speech Drawing',
        emoji: '🗣️',
        desc: 'A playful speech activity to help develop your child’s speech and pronunciation. Speak it, draw it — voice-powered fun strengthens vocabulary and confidence while sparking imagination.',
        youtube: 'nNjbXzxW3WU',
      },
      {
        title: 'Magic Drawing',
        emoji: '✨',
        desc: 'Doodles come alive with playful effects. The "wow" moment that keeps little artists creating again and again.',
        youtube: 'tLEfKzMGeDw',
      },
    ],
  },
  {
    id: 'math',
    title: 'Math Made Easy',
    icon: '🔢',
    items: [
      {
        title: 'Counting',
        emoji: '🧮',
        desc: 'Hands-on counting with bright visuals turns abstract numbers into something kids can see, touch, and love.',
        youtube: '1QfASzx8XuE',
      },
      {
        title: 'Addition',
        emoji: '➕',
        desc: 'First steps in math made joyful. Colorful animations make adding feel like a game, not a chore.',
        youtube: 'g_RleR24u4o',
      },
      {
        title: 'Subtraction',
        emoji: '➖',
        desc: 'A gentle introduction to taking away — confidence-building, with rewarding feedback every step.',
        youtube: 'KjpnmIR7F50',
      },
      {
        title: 'Multiplication',
        emoji: '✖️',
        desc: 'Multiplication made memorable. Fun patterns and visual rewards turn times tables into an adventure.',
        youtube: 'QKUMRyMvCsc',
      },
      {
        title: 'Division',
        emoji: '➗',
        desc: 'Sharing made simple. Stories and pictures kids actually understand, building real number sense.',
        youtube: '419_150rSf0',
      },
    ],
  },
  {
    id: 'worksheets',
    title: 'Multi-language Worksheets',
    icon: '📝',
    items: [
      {
        title: 'Letter Worksheet',
        emoji: '✏️',
        desc: 'Trace, write, repeat. Hands-on practice that strengthens early handwriting and letter recognition.',
        youtube: 'NPz0qEGhREQ',
      },
      {
        title: 'Number Worksheet',
        emoji: '🔢',
        desc: 'Number tracing and practice for confident pre-K math foundations — without tears.',
        youtube: 'pn5ESScPVoo',
      },
      {
        title: 'Writing Worksheet',
        emoji: '✍️',
        desc: 'Guided writing prompts and tracing activities that build steady hands and confident early writers.',
        youtube: '1XkzI4JTyg4',
      },
      {
        title: 'Puzzle Worksheet',
        emoji: '🧩',
        desc: 'Playful puzzle activities that sharpen problem-solving and pattern recognition — practice that feels like a game.',
        youtube: 'bXLIMCcOkow',
      },
    ],
  },
  {
    id: 'listening',
    title: 'Listening & Sound',
    icon: '🎯',
    items: [
      {
        title: 'Listening Activities',
        emoji: '🎧',
        desc: 'Audio-first lessons that build attention, memory, and comprehension — gentle skills with lifelong benefits.',
        youtube: 'ECmLFoOtqns',
      },
      {
        title: 'Sound Detective',
        emoji: '🔍',
        desc: 'Match sounds to objects in playful puzzles. A fun boost to auditory recognition and problem-solving.',
        youtube: 'E7NsxJkSpzs',
      },
    ],
  },
  {
    id: 'books',
    title: 'Multi-language Books',
    icon: '📚',
    items: [
      {
        title: 'Create Custom Books',
        emoji: '✨',
        desc: 'Build personalized animated books from scratch — turn letters, numbers, fruits, animals, or anything your child loves into their very own book.',
        youtube: 'DsB8PW1TUxQ',
      },
      {
        title: 'Letter Learning Book',
        emoji: '🔤',
        desc: 'Animated alphabet stories that bring every letter to life — preschool-ready and screen-friendly.',
        youtube: 'DH-6-Gxfo6g',
      },
      {
        title: 'Number Learning Book',
        emoji: '1️⃣',
        desc: 'Playful number stories help your child fall in love with counting from day one.',
        youtube: '1vLop--hJ74',
      },
    ],
  },
];

const moreFeatures = [
  {
    emoji: '🎒',
    title: 'Preschool Readiness',
    desc: 'Age-appropriate activities that build a strong foundation for the school years ahead.',
  },
  {
    emoji: '🌐',
    title: 'Multi-Language Learning',
    desc: 'Books, worksheets, alphabet, and numbers — available across 16 languages for an early gift of bilingualism.',
  },
  {
    emoji: '🖨️',
    title: 'Printable Learning Materials',
    desc: 'Print worksheets, books, and activity sheets at home for screen-free practice — perfect for the car, the table, or quiet time.',
  },
  {
    emoji: '⚙️',
    title: 'Parental Controls',
    desc: 'Stay in charge. Choose content, set limits, and keep everything organized for your family.',
  },
  {
    emoji: '☁️',
    title: 'Cloud Backup',
    desc: 'Every drawing, custom book, and creation stays safe — never lose a precious masterpiece.',
  },
  {
    emoji: '🔄',
    title: 'Sync Across Devices',
    desc: 'Phone, tablet, anywhere — pick up right where your child left off.',
  },
  {
    emoji: '🖼️',
    title: 'Gallery',
    desc: 'A beautiful collection of every artwork and custom book your child has made.',
  },
  {
    emoji: '🎙️',
    title: 'Voice Recording',
    desc: 'Record your own voice to narrate any book. Bedtime stories — even when you are away.',
  },
  {
    emoji: '👨‍👩‍👧',
    title: 'Family & Friends Books',
    desc: 'Make personal books starring the people your child loves the most.',
  },
  {
    emoji: '📖',
    title: 'Custom Animated Books',
    desc: 'Turn letters, fruits, flowers — anything — into a personalized animated book.',
  },
  {
    emoji: '🌈',
    title: 'Color Learning',
    desc: 'Discover colors through cheerful, hands-on animations that delight.',
  },
  {
    emoji: '🎮',
    title: 'Educational Games',
    desc: 'Tic-Tac-Toe, puzzles, and brain games featuring favorite characters — fun that teaches.',
  },
  {
    emoji: '🛡️',
    title: 'Ad-Free Subscription',
    desc: 'Pick our optional ad-free subscription for a fully distraction-free space — secure, calm, and built for focused learning.',
  },
];

const supportedLanguages = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
  { code: 'it', name: 'Italian', flag: '🇮🇹' },
  { code: 'nl', name: 'Dutch', flag: '🇳🇱' },
  { code: 'sv', name: 'Swedish', flag: '🇸🇪' },
  { code: 'no', name: 'Norwegian', flag: '🇳🇴' },
  { code: 'ru', name: 'Russian', flag: '🇷🇺' },
  { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
  { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
  { code: 'bn', name: 'Bengali', flag: '🇧🇩' },
  { code: 'id', name: 'Indonesian', flag: '🇮🇩' },
  { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
  { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
  { code: 'ko', name: 'Korean', flag: '🇰🇷' },
];

const extractYouTubeId = (value) => {
  if (!value) return '';
  if (/^[a-zA-Z0-9_-]{11}$/.test(value)) return value;
  const match = value.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([a-zA-Z0-9_-]{11})/
  );
  return match ? match[1] : '';
};

const VideoCard = ({ item }) => {
  const [playing, setPlaying] = useState(false);
  const videoId = extractYouTubeId(item.youtube);
  const hasVideo = Boolean(videoId);

  let thumb;
  if (hasVideo && playing) {
    thumb = (
      <div className="video-thumb video-thumb--playing">
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&playsinline=1&modestbranding=1`}
          title={`${item.title} demo`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    );
  } else if (hasVideo) {
    thumb = (
      <button
        type="button"
        className="video-thumb"
        onClick={() => setPlaying(true)}
        aria-label={`Play ${item.title} demo`}
      >
        <img
          src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
          alt=""
          width="480"
          height="360"
          loading="lazy"
          decoding="async"
        />
        <span className="video-play" aria-hidden="true">▶</span>
      </button>
    );
  } else {
    thumb = (
      <div className="video-thumb video-thumb--static">
        <div className="video-thumb-placeholder" aria-hidden="true">
          <span className="video-thumb-emoji">{item.emoji}</span>
        </div>
        <span className="video-play video-play--muted" aria-hidden="true">▶</span>
        <span className="video-coming-soon">Demo coming soon</span>
      </div>
    );
  }

  return (
    <article className="video-feature-card">
      {thumb}
      <div className="video-feature-body">
        <h4>
          <span className="video-feature-emoji">{item.emoji}</span>
          {item.title}
        </h4>
        <p>{item.desc}</p>
      </div>
    </article>
  );
};

const Home = () => {
  return (
    <div className="home">
      <header className="header">
        <div className="container">
          <div className="header-left">
            <h1 className="logo">
              <img src="/assets/ic_kidstrail.png" alt="KidsTrail" className="logo-icon" />
              KidsTrail
            </h1>
          </div>
          <div className="header-center">
            <div className="header-download-buttons">
              <a
                href="https://apps.apple.com/app/id6749541872"
                className="header-download-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/assets/ic_applestore.png" alt="Download on App Store" className="header-store-icon" />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.kidstrail.app"
                className="header-download-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/assets/ic_playstore.png" alt="Get it on Google Play" className="header-store-icon" />
              </a>
            </div>
            <div className="header-social-buttons">
              <a
                href="https://www.youtube.com/@KidsTrailCare"
                className="header-social-btn"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
              >
                <img src="/assets/ic_youtube.png" alt="YouTube" className="header-social-icon" />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61585649347570"
                className="header-social-btn"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <img src="/assets/ic_facebook.png" alt="Facebook" className="header-social-icon" />
              </a>
              <a
                href="https://www.instagram.com/kidstrail.care/"
                className="header-social-btn"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <img src="/assets/ic_instagram.png" alt="Instagram" className="header-social-icon" />
              </a>
            </div>
          </div>
        </div>
      </header>

      <main className="main">
        <section className="hero">
          <div className="container">
            <div className="hero-content">
              <span className="hero-eyebrow">Made for curious kids · Loved by parents</span>
              <h2 className="hero-title">
                Where Learning Becomes a Joyful{' '}
                <span className="hero-title-tail">Adventure 🌈</span>
              </h2>
              <p className="hero-tagline">
                Kids learning app with preschool prep, speech activities, drawing, writing
                practice, books, worksheets, quizzes, brain games, parental controls, printable
                learning materials, and much more.
              </p>
            </div>
          </div>
        </section>

        {featureCategories.map((category) => (
          <section className="feature-category" key={category.id} id={`category-${category.id}`}>
            <div className="container">
              <div className="category-header">
                <div className="category-heading">
                  <span className="category-icon" aria-hidden="true">{category.icon}</span>
                  <h3>{category.title}</h3>
                </div>
              </div>
              <div className="video-feature-grid">
                {category.items.map((item) => (
                  <VideoCard key={item.title} item={item} />
                ))}
              </div>
            </div>
          </section>
        ))}

        <section className="more-features">
          <div className="container">
            <div className="more-features-header">
              <h3 className="more-features-title">Built for Parents Who Care</h3>
              <p className="more-features-subtitle">
                All the safety, organization, and convenience your family needs — in one place.
              </p>
            </div>
            <div className="more-features-grid">
              {moreFeatures.map((feature) => (
                <div className="more-feature-card" key={feature.title}>
                  <span className="more-feature-emoji" aria-hidden="true">{feature.emoji}</span>
                  <h4>{feature.title}</h4>
                  <p>{feature.desc}</p>
                </div>
              ))}
              <div className="more-feature-card languages-card">
                <span className="more-feature-emoji" aria-hidden="true">🌐</span>
                <h4>Available in {supportedLanguages.length} Languages</h4>
                <p>Books, worksheets, alphabet, and numbers — practice in your child&rsquo;s language.</p>
                <ul className="language-chips">
                  {supportedLanguages.map((lang) => (
                    <li className="language-chip" key={lang.code}>
                      <span className="language-flag" aria-hidden="true">{lang.flag}</span>
                      <span>{lang.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="download-section">
          <div className="container">
            <h3 className="download-title">Start the Adventure Today</h3>
            <p className="download-subtitle">
              Available on iOS and Android — free to download and easy to set up.
            </p>
            <div className="download-buttons">
              <a
                href="https://apps.apple.com/app/id6749541872"
                className="download-btn ios-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="btn-content">
                  <img src="/assets/ic_applestore.png" alt="Download on App Store" className="store-icon" />
                </div>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.kidstrail.app"
                className="download-btn android-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="btn-content">
                  <img src="/assets/ic_playstore.png" alt="Get it on Google Play" className="store-icon" />
                </div>
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="container">
          <p>&copy; 2026 KidsTrail. All rights reserved.</p>
          <div className="footer-links">
            <Link to="/contact-us" className="footer-link">Contact Us</Link>
            <Link to="/terms-of-use" className="footer-link">Terms of Use</Link>
            <Link to="/privacy-policy" className="footer-link">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
